/* eslint-disable */

import { useEffect, useState } from 'react'
import { ChevronLeft } from 'lucide-react'
import { Button } from '../Components/ui/button'
import { Input } from '../Components/ui/input'
import { Label } from '../Components/ui/label'
import { Checkbox } from '../Components/ui/checkbox'
import env from '../env/env'
import { get, post } from '../http/restservice'
import { userDetailsState } from './LoginState'
import { useRecoilState } from 'recoil'
import { useLocation, useNavigate } from 'react-router-dom'
import { cleanIndianPhoneNumber, formatIndianNumber } from '../utils/Util'
import { useModal } from '../hooks/use-modal-store'
import { useErrorPopup } from '../hooks/useErrorPopup'
import BarLoader from '../Components/BarLoader'
import { getUserDetails } from '../utils/AuthUtil'
import { v4 as uuidv4 } from 'uuid'

const KycLanding = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search)
  const elgStatus = queryParams?.get('STATUS')
  const [userDetails, setUserDetails] = useRecoilState(userDetailsState)
  const [checked, setChecked] = useState(false)
  const [panNumber, setPanNumber] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [loading, setLoading] = useState(false)
  const { onOpen } = useModal()
  const { showError } = useErrorPopup()
  const [showElogLoading, setShowElogLoading] = useState(false)

  //! Testing Purpose
  // useEffect(() => {
  //   onOpen('openKycPopup', {
  //     title: 'KYC Updated!',
  //     message:
  //       "We've received your updated KYC details. Sit tight while we review your documents. ",
  //     severity: 'needs_review',
  //     handleRedirect: () => navigate('/dashboard'),
  //   })
  // }, [])

  useEffect(() => {
    const details = getUserDetails()
    if (details && Object.keys(details).length) {
      setUserDetails(details)
    }
  }, [location])

  useEffect(() => {
    const elogStatus = location?.state?.isElogDone

    if (elogStatus === false && userDetails) {
      setShowElogLoading(true)
      const timer = setTimeout(() => {
        handleElog()
      }, 5000)

      return () => clearTimeout(timer) // Cleanup on unmount
    }
  }, [location?.state?.isElogDone, userDetails])

  useEffect(() => {
    const checkAndUpdateStatus = async () => {
      if (
        elgStatus?.toLowerCase() === 'success' &&
        userDetails &&
        Object.keys(userDetails).length
      ) {
        const { kycPhone } = await fetchCustomerKycDetails(userDetails?.phoneNumber)

        if (kycPhone) {
          updateElogStatusAndKycNumber(kycPhone)
        }
      } else if (elgStatus?.toLowerCase() === 'pending') {
        showError(
          'Authorization Pending',
          'Your request is still being processed. Please wait a few minutes and try again. If the issue persists, contact support.',
          () => navigate('/dashboard'),
        )
      } else if (elgStatus?.toLowerCase() === 'failure') {
        showError(
          'BSE Authentication Failed',
          'We were unable to complete your eLog authentication with BSE. Please try again after some time. If the issue persists, contact our support team for assistance.',
          () => navigate('/dashboard'),
        )
      }
    }

    checkAndUpdateStatus()
  }, [location.search, userDetails])

  const handleCheckedChange = (newChecked) => {
    setChecked(newChecked)
  }

  const handleDateChange = (e) => {
    const value = e.target.value.replace(/\D/g, '') // Remove non-digits

    // Format with slashes
    let formattedValue = ''

    if (value.length > 0) {
      formattedValue += value.substring(0, Math.min(2, value.length))
    }

    if (value.length > 2) {
      formattedValue += '-' + value.substring(2, Math.min(4, value.length))
    }

    if (value.length > 4) {
      formattedValue += '-' + value.substring(4, Math.min(8, value.length))
    }

    setDateOfBirth(formattedValue)
  }

  const handlePanChange = (e) => {
    const value = e.target.value.toUpperCase()
    let formattedValue = ''

    // Remove any characters that don't match the expected pattern
    for (let i = 0; i < value.length && i < 10; i++) {
      const char = value[i]

      if (i < 5) {
        // First 5 positions: only alphabets
        if (/[A-Z]/.test(char)) {
          formattedValue += char
        }
      } else if (i < 9) {
        // Next 4 positions: only numbers
        if (/[0-9]/.test(char)) {
          formattedValue += char
        }
      } else if (i === 9) {
        // Last position: only alphabet
        if (/[A-Z]/.test(char)) {
          formattedValue += char
        }
      }
    }

    setPanNumber(formattedValue)
  }

  const isPanValid = (pan) => {
    if (pan.length !== 10) return false

    // Check first 5 characters are alphabets
    for (let i = 0; i < 5; i++) {
      if (!/[A-Z]/.test(pan[i])) return false
    }

    // Check next 4 characters are numbers
    for (let i = 5; i < 9; i++) {
      if (!/[0-9]/.test(pan[i])) return false
    }

    // Check last character is alphabet
    if (!/[A-Z]/.test(pan[9])) return false

    return true
  }

  const startKYC = async (e) => {
    e?.preventDefault()

    setLoading(true)
    try {
      const transactionId =
        typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : require('uuid').v4() // Generate unique transaction ID
      const workflowId = 'yGguwb_21_05_25_15_23_04' // Replace with your workflow ID
      // yGguwb_21_05_25_15_23_04 // short flow
      // HeHXEm_09_06_25_22_36_54 // dynamic
      // 0EbWbt_03_04_25_17_43_33
      // fZlgIt_21_05_25_18_34_30
      // VyE9BW_30_04_25_14_29_28 // non-validated

      const res = await post(`${env.UAT_URL}customerservice/api/hyperverge/accessToken`)
      console.log('🚀 ~ startKYC ~ res:', res)

      const token = res?.token

      if (window.HyperKycConfig && window.HyperKYCModule) {
        const hyperKycConfig = new window.HyperKycConfig(token, workflowId, transactionId)

        // Conditionally add emailId
        const email = userDetails?.email

        // Build input object
        const inputs = {
          mobileNumber: formatIndianNumber(userDetails?.phoneNumber),
          panNumber: panNumber,
          dob: dateOfBirth,
          emailId: email.includes('fabits') ? 'null' : email,
        }

        // Set inputs
        hyperKycConfig.setInputs(inputs)

        window.HyperKYCModule.launch(hyperKycConfig, handler)
      } else {
        alert('HyperVerge SDK not loaded properly.')
      }
    } catch (error) {
      console.log('Failed to fetch token:', error)

      onOpen('openKycPopup', {
        title: 'Token Fetch Failed',
        message:
          'We were unable to fetch the required authorization token to create your investment plan. Please try again in a few minutes.',
        severity: 'failure',
        handleRedirect: () => {},
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRetry = () => {
    if (panNumber && dateOfBirth) {
      startKYC()
    } else {
      return () => {}
    }
  }

  const triggerElogAfterDelay = async () => {
    if (!userDetails?.phoneNumber) return

    // Wait for 10 seconds
    await new Promise((resolve) => setTimeout(resolve, 10000))

    try {
      const kycDetails = await fetchCustomerKycDetails(userDetails?.phoneNumber)

      if (kycDetails?.kycPhone) {
        handleElog()
      }
    } catch (error) {
      console.error('Error fetching KYC details after delay:', error)
    }
  }

  const handler = async (HyperKycResult) => {
    console.log('🚀 ~ handler ~ HyperKycResult:', HyperKycResult)
    switch (HyperKycResult.status) {
      // ----Incomplete workflow-----
      case 'user_cancelled':
        onOpen('openKycPopup', {
          title: 'KYC cancelled !',
          message:
            'Looks like your KYC was cancelled. Complete it today and take the next step toward achieving your goals.',
          severity: 'user_cancelled',
          handleRedirect: handleRetry,
        })

        console.log('KYC Cancelled by User:', HyperKycResult)
        break

      case 'error':
        onOpen('openKycPopup', {
          title: 'Please try again!',
          message: 'We’re currently experiencing an issue. Please try again after some time.',
          severity: 'failure',
          handleRedirect: () => {},
        })
        console.error('KYC Error:', HyperKycResult)
        break

      // ----Complete workflow-----
      case 'auto_approved':
        setShowElogLoading(true)
        triggerElogAfterDelay()
        console.log('KYC Auto Approved:', HyperKycResult)
        break

      case 'auto_declined':
        onOpen('openKycPopup', {
          title: 'KYC Declined!',
          message:
            'No worries—your KYC didn’t go through this time. Please upload the correct documents and try again.',
          severity: 'auto_declined',
          handleRedirect: () => {},
        })

        console.warn('KYC Auto Declined:', HyperKycResult)
        break

      case 'needs_review':
        setShowElogLoading(true)
        triggerElogAfterDelay()
        console.log('KYC Needs Review:', HyperKycResult)

        break

      default:
        console.warn('Unhandled KYC Status:', HyperKycResult.status)
        break
    }
  }

  const fetchCustomerKycDetails = async (phNum) => {
    const phNo = phNum?.replace(/^(\+91\s?)/, '')

    try {
      const response = await get(`${env.UAT_URL}customerservice/api/customer?phoneNumber=${phNo}`)
      console.log('🚀 ~ fetchCustomerKycDetails ~ response:', response)

      if (!response || response?.isError) {
        showError(
          'We Couldn’t Load Your KYC Info',
          'Something went wrong while fetching your KYC details. Please try again in a few moments or reach out to support if the issue continues.',
          () => navigate('/dashboard'),
        )
        return { ucc: null, kycPhone: null }
      }

      const uid = response?.uid
      const ucc = uid?.includes('-') ? uid.split('-')[0] : null
      const kycPhone = response?.kycPhoneNumber ?? null

      if (!kycPhone) {
        onOpen('openKycPopup', {
          title: 'BSE Authentication Pending',
          message:
            'Your KYC is complete, but we couldn’t finish the final step with BSE. Please try again after some time to complete the authentication.',
          severity: 'auto_approved',
          handleRedirect: () => navigate('/dashboard'),
        })

        return { ucc, kycPhone: null }
      }

      return { ucc, kycPhone }
    } catch (error) {
      console.error('Error in fetchCustomerKycDetails:', error)

      showError(
        'Unexpected Error',
        'Something went wrong while fetching your KYC details. Please try again later.',
        () => navigate('/dashboard'),
      )
      return null
    }
  }

  const handleElog = async () => {
    const result = await fetchCustomerKycDetails(userDetails?.phoneNumber)
    console.log('🚀 ~ handleElog ~ result:', result)
    if (!result) {
      showError(
        'Unable to Retrieve KYC Details',
        'We couldn’t fetch your KYC information. Please try again later.',
        () => navigate('/dashboard'),
      )
      return
    }

    const { ucc, kycPhone } = result

    if (!kycPhone) {
      // showError(
      //   'Unable to Retrieve KYC Details',
      //   'Please try again later or contact support.',
      //   () => navigate('/dashboard'),
      // )
      return
    }

    const payload = {
      clientCode: ucc?.toLowerCase(),
      loopbackUrl: `${env.LOOPBACK_URL}dashboard/start-kyc`,
      allowLoopbackMsg: 'Y',
    }

    const response = await post(
      `${env.UAT_URL}mutualfundservice/api/bseStar/api/elogAuthentication`,
      payload,
    )
    console.log('🚀 ~ handleElog ~ response:', response)

    // Handle specific ELOG errors
    if (
      response?.isError &&
      response?.response?.data?.errordesc?.includes('FAILED: AUTHENTICATION IS ALREADY DONE')
    ) {
      onOpen('openKycPopup', {
        title: 'Authentication Already Completed!',
        message: 'Your BSE account authentication is already done.',
        severity: 'auto_approved',
        handleRedirect: () => navigate('/dashboard'),
      })
      return
    }

    if (response?.isError) {
      onOpen('openKycPopup', {
        title: 'Please try again!',
        message: 'Your account confirmation wasn’t successful. Please try again later.',
        severity: 'failure',
        handleRedirect: () => {},
      })
      return
    }

    if (
      !response?.isError &&
      response?.status === 'SUCCESS' &&
      response?.data?.errordesc?.includes('ELOG Link Generated Successfully')
    ) {
      window.open(response?.data?.authurl, '_self')
      setShowElogLoading(false)
    }
  }

  const updateElogStatusAndKycNumber = async (phNo) => {
    const payload = {
      kycPhoneNumber: phNo,
      isElogDone: elgStatus === 'SUCCESS' ? true : false,
    }

    const response = await post(
      `${env.UAT_URL}customerservice/api/customer/updateElogStatus`,
      payload,
    )

    if (response?.isError) {
      onOpen('openKycPopup', {
        title: '',
        message: 'We were unable to process your request at the moment. Please try again later.',
        severity: 'failure',
        handleRedirect: () => navigate('/dashboard'),
      })

      return
    }

    const isPhoneNoSame =
      cleanIndianPhoneNumber(phNo) === cleanIndianPhoneNumber(userDetails?.phoneNumber)
        ? false
        : true

    if (!isPhoneNoSame) {
      onOpen('openKycPopup', {
        title: 'Account Approved!',
        message: 'Your BSE authentication is successful and your account is now approved.',
        severity: 'auto_approved',
        isKycPhUpdate: false,
        handleRedirect: () => navigate('/dashboard'),
      })
    }

    if (isPhoneNoSame && !response?.isError) {
      const updateKycNumber = await post(
        `${env.UAT_URL}customerservice/api/customer/updatePrimaryPhoneNumber`,
        {
          phoneNumber: userDetails?.phoneNumber,
          kycPhoneNumber: phNo,
        },
      )

      if (updateKycNumber?.isError) {
        onOpen('openKycPopup', {
          title: 'Update Failed',
          message:
            'We were unable to update your phone number and confirm your account. Please try again later.',
          severity: 'failure',
          handleRedirect: () => navigate('/dashboard'),
        })
      }

      if (!updateKycNumber?.isError) {
        onOpen('openKycPopup', {
          title: 'Account Approved',
          message:
            'Your BSE authentication is already complete, and your phone number has been updated successfully.',
          severity: 'auto_approved',
          isKycPhUpdate: true,
          kycPhoneNumber: phNo,
          handleRedirect: () => navigate('/dashboard'),
        })
      }
    }
  }

  // Check if form is valid
  const isFormValid = isPanValid(panNumber) && dateOfBirth.length === 10 // DD/MM/YYYY = 10 characters

  return (
    <>
      {showElogLoading ? (
        <div className='flex flex-col space-y-10 justify-center items-center h-screen px-20 text-center'>
          <BarLoader />
          <h1 className='font-semibold text-xl leading-7 tracking-[-4%]'>
            Approve your account with Bombay Stock Exchange
          </h1>
        </div>
      ) : (
        <div className='flex items-center justify-center md:p-4'>
          <div className='h-[calc(100vh-100px)] flex flex-col w-full lg:max-w-md bg-white lg:rounded-lg lg:shadow-sm lg:border'>
            {/* Header */}
            <div
              className='flex items-center font-semibold text-black-500 p-4 border-b cursor-pointer'
              onClick={() => navigate('/dashboard')}
            >
              <ChevronLeft className='h-5 w-5  mr-3' />
              <h1>Complete KYC</h1>
            </div>

            {/* Content */}
            <div className='flex-1 flex flex-col justify-center p-6 space-y-6'>
              <div className='space-y-4'>
                {/* Coin Image */}
                <div className='flex justify-center mt-10'>
                  <div className='relative'>
                    <img
                      className='h-18 w-18'
                      src={`${env.S3_BASE_URL}/firstTime/question4.svg`}
                      alt={'start kyc'}
                    />
                  </div>
                </div>

                {/* Title and Description */}
                <div className='text-center space-y-2'>
                  <h2 className='text-xl font-semibold text-black-500'>Enter PAN number</h2>
                  <p className='text-sm text-black-400'>
                    Please provide the necessary details to set up your KYC
                  </p>
                </div>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='pan-number' className='text-sm text-black-300'>
                  Pan card number <span className='text-error-500'>*</span>
                </Label>
                <Input
                  id='pan-number'
                  type='text'
                  value={panNumber}
                  onChange={handlePanChange}
                  className='w-full px-3 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-transparent'
                  placeholder='Enter PAN number'
                  required
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='date-of-birth' className='text-sm text-black-300'>
                  Date of birth <span className='text-error-500'>*</span>
                </Label>
                <Input
                  id='date-of-birth'
                  type='text'
                  value={dateOfBirth}
                  onChange={handleDateChange}
                  className='w-full px-3 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-transparent'
                  placeholder='DD-MM-YYYY'
                  required
                  maxLength={10}
                  inputMode='numeric'
                />
              </div>
            </div>

            <div className='flex items-center space-x-2 px-5 py-4'>
              {/* <Checkbox id='terms' checked={checked} onCheckedChange={handleCheckedChange} /> */}
              <Label
                htmlFor='terms'
                className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
              >
                By proceeding, you agree to the{' '}
                <a href='#' className='underline underline-offset-4 hover:text-primary'>
                  Terms and Conditions.
                </a>
              </Label>
            </div>

            <div className='flex-shrink-0 p-4'>
              {/* Form */}
              <form onSubmit={startKYC} className='space-y-4'>
                <Button
                  loading={loading}
                  onClick={startKYC}
                  disabled={!isFormValid}
                  type='submit'
                  className='w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-medium transition-colors disabled:bg-primary-300 disabled:cursor-not-allowed'
                >
                  Submit
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default KycLanding
